/**
 * Atlas PM · 系统设置 · 任务模板 · 编辑弹窗（P8.2）。
 *
 * ⚠️ 只改「基本信息」：名称 / 说明 / 范围（个人↔公共）/ 启用状态；
 *    ⚠️ 不改模板内的指标与要素结构（模板是创建时的结构快照）。
 * ⚠️ 无 template:manage 权限者：范围中的「公共」置灰并说明原因。
 * ⚠️ 保存中禁用按钮，防重复提交。
 */

import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import FormField from '../../../components/FormField';
import Modal from '../../../components/Modal';
import { ApiError } from '../../../api/client';
import { useAuthStore } from '../../../stores/auth';
import { type TaskTemplateItem, type TemplateScope } from './api';
import { useUpdateTaskTemplate } from './useTaskTemplates';

export interface TaskTemplateDialogProps {
  open: boolean;
  template: TaskTemplateItem | null;
  onClose(): void;
  onSaved?(item: TaskTemplateItem): void;
}

interface FormState {
  name: string;
  description: string;
  scope: TemplateScope;
  isActive: boolean;
}

const EMPTY: FormState = {
  name: '',
  description: '',
  scope: 'PERSONAL',
  isActive: true,
};

export default function TaskTemplateDialog({
  open,
  template,
  onClose,
  onSaved,
}: TaskTemplateDialogProps) {
  const canManagePublic = useAuthStore((s) => s.hasPermission('template:manage'));
  const updateMut = useUpdateTaskTemplate(template?.id ?? '');
  const busy = updateMut.isPending;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 每次打开时按当前条目回填
  useEffect(() => {
    if (!open || !template) return;
    setErrors({});
    setSubmitError(null);
    setForm({
      name: template.name,
      description: template.description ?? '',
      scope: template.scope,
      isActive: template.isActive,
    });
  }, [open, template]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'name') setErrors((e) => ({ ...e, name: undefined }));
    setSubmitError(null);
  };

  const onSubmit = () => {
    const nextErrors: { name?: string } = {};
    const name = form.name.trim();
    if (!name) nextErrors.name = '名称不能为空';
    else if (name.length > 60) nextErrors.name = '名称不能超过 60 字';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    updateMut.mutate(
      {
        name,
        description: form.description.trim(),
        scope: form.scope,
        isActive: form.isActive,
      },
      {
        onSuccess: (saved) => {
          onSaved?.(saved);
          onClose();
        },
        onError: (err) =>
          setSubmitError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : '保存失败',
          ),
      },
    );
  };

  return (
    <Modal
      open={open}
      title={`编辑任务模板 · ${template?.name ?? ''}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            取消
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={busy} data-testid="tpl-submit">
            {busy ? '提交中…' : '保存修改'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        {submitError && <div className="pm-form__error">⚠ {submitError}</div>}

        <div className="tpl-form-grid">
          <FormField label="名称" required htmlFor="tpl-name" error={errors.name}>
            <input
              id="tpl-name"
              className="pm-input"
              data-testid="tpl-field-name"
              placeholder="模板名称"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </FormField>

          <FormField label="状态" htmlFor="tpl-active">
            <label className="tpl-check">
              <input
                id="tpl-active"
                type="checkbox"
                data-testid="tpl-field-active"
                checked={form.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
              />
              启用（停用后不出现在「从模板新建」的可用列表）
            </label>
          </FormField>

          <FormField
            label="说明"
            className="tpl-form-col--full"
            hint="最多 200 字，可留空"
          >
            <textarea
              className="pm-input"
              data-testid="tpl-field-desc"
              rows={3}
              maxLength={200}
              placeholder="用于说明模板的适用场景"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
          </FormField>

          <FormField
            label="范围"
            className="tpl-form-col--full"
            hint={
              canManagePublic
                ? '个人：仅自己可见；公共：所有用户可见'
                : '无模板管理权限，不能设为「公共」（如需公共模板请联系超级管理员）'
            }
          >
            <div className="tpl-scope">
              <label className="tpl-scope__opt">
                <input
                  type="radio"
                  name="tpl-scope"
                  data-testid="tpl-scope-personal"
                  checked={form.scope === 'PERSONAL'}
                  onChange={() => setField('scope', 'PERSONAL')}
                />
                个人
              </label>
              <label className={`tpl-scope__opt${canManagePublic ? '' : ' is-disabled'}`}>
                <input
                  type="radio"
                  name="tpl-scope"
                  data-testid="tpl-scope-public"
                  checked={form.scope === 'PUBLIC'}
                  disabled={!canManagePublic}
                  onChange={() => setField('scope', 'PUBLIC')}
                />
                公共
              </label>
            </div>
          </FormField>
        </div>

        <div className="tpl-form-note">
          ⚠ 模板只承载创建时的「指标 + 要素」结构快照，本页不改结构。
        </div>
      </form>
    </Modal>
  );
}
